import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { renderPdfThumbnails, renderPdfPageThumbnail, mergePdfPages } from '../services/pdfOrganizer';
import { PageEdits, emptyEdits, rotateEdits90 } from '../services/pdfEdit';
import PdfPageEditor from './PdfPageEditor';

// A page placed in the organizer. `rotation` is the extra rotation the user has
// applied; `edits` are the text + box annotations. Both are rendered into the
// thumbnail and baked into the exported PDF.
interface OrganizerPage {
  id: string;
  fileId: string;
  fileName: string;
  pageIndex: number; // 0-based page within the source PDF
  rotation: number; // 0/90/180/270
  edits: PageEdits;
  thumbnail: string; // PNG data URL
}

const CARD_WIDTH = 200;

interface SortablePageProps {
  page: OrganizerPage;
  index: number;
  onRotate: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
}

// A single draggable/sortable page card. The preview area is the drag handle;
// the action buttons stay outside it so their clicks aren't captured by a drag.
const SortablePage: React.FC<SortablePageProps> = ({ page, index, onRotate, onDelete, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 8 : 2}
      sx={{ width: CARD_WIDTH, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Drag handle + preview */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          position: 'relative',
          backgroundColor: 'grey.100',
          p: 1,
          display: 'flex',
          justifyContent: 'center',
          touchAction: 'none',
        }}
      >
        <Chip
          label={index + 1}
          size="small"
          color="primary"
          sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
        />
        <DragIndicatorIcon
          fontSize="small"
          sx={{ position: 'absolute', top: 6, right: 4, color: 'text.disabled' }}
        />
        <Box
          component="img"
          src={page.thumbnail}
          alt={`Page ${index + 1}`}
          sx={{
            maxWidth: '100%',
            maxHeight: 260,
            objectFit: 'contain',
            boxShadow: 1,
            backgroundColor: '#fff',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* Caption + per-page actions */}
      <Box sx={{ p: 1 }}>
        <Tooltip title={`${page.fileName} — page ${page.pageIndex + 1}`}>
          <Typography variant="caption" noWrap sx={{ display: 'block' }}>
            {page.fileName} · p{page.pageIndex + 1}
          </Typography>
        </Tooltip>
        <Stack direction="row" justifyContent="flex-end">
          <Tooltip title="Edit text">
            <IconButton size="small" color="primary" onClick={() => onEdit(page.id)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rotate 90°">
            <IconButton size="small" onClick={() => onRotate(page.id)}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove page">
            <IconButton size="small" color="error" onClick={() => onDelete(page.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
};

const PdfOrganizer: React.FC = () => {
  const [pages, setPages] = useState<OrganizerPage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);

  // Original PDF bytes per source file, kept for the final pdf-lib export.
  const sourcesRef = useRef<Record<string, Uint8Array>>({});
  // Monotonic counter for stable, unique ids (used for both files and pages).
  const idCounter = useRef(0);
  const nextId = () => `id-${++idCounter.current}`;

  const addFiles = async (files: File[]) => {
    const pdfs = files.filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfs.length === 0) {
      setError('Please select PDF files only.');
      return;
    }

    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const added: OrganizerPage[] = [];
      for (const file of pdfs) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const fileId = nextId();
        sourcesRef.current[fileId] = bytes;

        const thumbnails = await renderPdfThumbnails(bytes);
        thumbnails.forEach((thumbnail, pageIndex) => {
          added.push({
            id: nextId(),
            fileId,
            fileName: file.name,
            pageIndex,
            rotation: 0,
            edits: emptyEdits(),
            thumbnail,
          });
        });
      }
      setPages((prev) => [...prev, ...added]);
      setSuccess(`Added ${added.length} page${added.length === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error('Error loading PDF(s):', err);
      setError(err instanceof Error ? err.message : 'Failed to load PDF.');
    } finally {
      setBusy(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (accepted) => addFiles(accepted),
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  const sensors = useSensors(
    // A small drag threshold so the rotate/delete buttons still register as clicks.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPages((prev) => {
      const oldIndex = prev.findIndex((p) => p.id === active.id);
      const newIndex = prev.findIndex((p) => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleRotate = async (id: string) => {
    const target = pages.find((p) => p.id === id);
    if (!target) return;
    const rotation = (target.rotation + 90) % 360;
    // Rotate any edits so they stay glued to the page, then refresh the thumbnail.
    const edits = rotateEdits90(target.edits);
    setPages((prev) => prev.map((p) => (p.id === id ? { ...p, rotation, edits } : p)));
    try {
      const thumbnail = await renderPdfPageThumbnail(
        sourcesRef.current[target.fileId],
        target.pageIndex + 1,
        rotation,
        edits
      );
      setPages((prev) => prev.map((p) => (p.id === id ? { ...p, thumbnail } : p)));
    } catch (err) {
      console.error('Error rotating page:', err);
    }
  };

  const handleEdit = (id: string) => {
    setEditingPageId(id);
  };

  const handleEditorSave = async (edits: PageEdits) => {
    const target = pages.find((p) => p.id === editingPageId);
    setEditingPageId(null);
    if (!target) return;
    setPages((prev) => prev.map((p) => (p.id === target.id ? { ...p, edits } : p)));
    try {
      const thumbnail = await renderPdfPageThumbnail(
        sourcesRef.current[target.fileId],
        target.pageIndex + 1,
        target.rotation,
        edits
      );
      setPages((prev) => prev.map((p) => (p.id === target.id ? { ...p, thumbnail } : p)));
    } catch (err) {
      console.error('Error updating edited page:', err);
    }
  };

  const handleDelete = (id: string) => {
    setPages((prev) => {
      const next = prev.filter((p) => p.id !== id);
      // Free any source files that no longer have pages in the document.
      const stillUsed = new Set(next.map((p) => p.fileId));
      Object.keys(sourcesRef.current).forEach((fileId) => {
        if (!stillUsed.has(fileId)) delete sourcesRef.current[fileId];
      });
      return next;
    });
  };

  const handleClearAll = () => {
    setPages([]);
    sourcesRef.current = {};
    setError(null);
    setSuccess(null);
  };

  const handleDownload = async () => {
    if (pages.length === 0) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const blob = await mergePdfPages(
        pages.map((p) => ({
          fileId: p.fileId,
          pageIndex: p.pageIndex,
          rotation: p.rotation,
          edits: p.edits,
        })),
        sourcesRef.current
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'organized.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess('Merged PDF downloaded.');
    } catch (err) {
      console.error('Error merging PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to merge PDF.');
    } finally {
      setBusy(false);
    }
  };

  const editingPage = pages.find((p) => p.id === editingPageId) || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Organize &amp; Edit PDF Pages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload one or more PDFs, drag the pages into the order you want, add text with the edit button,
          and download a single merged PDF. You can keep adding files at any time if a page is missing.
        </Typography>
      </Box>

      {/* Drop zone — always available so pages can be added at any time. */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon color="action" sx={{ fontSize: 40, mb: 1 }} />
        <Typography gutterBottom>
          {isDragActive ? 'Drop the PDFs here…' : 'Drag & drop PDFs here, or'}
        </Typography>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={open} disabled={busy}>
          {pages.length > 0 ? 'Add More PDFs' : 'Select PDFs'}
        </Button>
      </Box>

      {/* Action bar */}
      {pages.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1">
            {pages.length} page{pages.length === 1 ? '' : 's'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" startIcon={<ClearAllIcon />} onClick={handleClearAll} disabled={busy}>
              Clear All
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={busy}
            >
              Download Merged PDF
            </Button>
          </Stack>
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {/* Page grid with drag-and-drop reordering (dnd-kit handles wrapping rows). */}
      {pages.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={pages.map((p) => p.id)} strategy={rectSortingStrategy}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              {pages.map((page, index) => (
                <SortablePage
                  key={page.id}
                  page={page}
                  index={index}
                  onRotate={handleRotate}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      )}

      {/* Per-page text editor */}
      {editingPage && (
        <PdfPageEditor
          open
          bytes={sourcesRef.current[editingPage.fileId]}
          pageNumber={editingPage.pageIndex + 1}
          rotation={editingPage.rotation}
          fileName={editingPage.fileName}
          initialEdits={editingPage.edits}
          onCancel={() => setEditingPageId(null)}
          onSave={handleEditorSave}
        />
      )}
    </Box>
  );
};

export default PdfOrganizer;
